import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { InstallAppButton } from "@/components/umaang/InstallPrompt";
import { Download, Smartphone, ArrowLeft, Zap, Bell, WifiOff, Sparkles, Share2, MoreVertical, Plus, Monitor, ArrowUpFromLine } from "lucide-react";

// Local copies of platform-detection + steps (kept lightweight; identical UX to InstallPrompt modal).

function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  const isIOSChrome = /crios/i.test(ua);
  const isIOSFirefox = /fxios/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isSamsung = /samsungbrowser/i.test(ua);
  const isFirefoxAndroid = isAndroid && /firefox/i.test(ua);
  const isChromeDesktop = /chrome|chromium|crios/i.test(ua) && !isAndroid && !isIOSChrome;
  const isEdge = /edg\//i.test(ua);
  const isSafariDesktop = /^((?!chrome|android|crios).)*safari/i.test(ua) && !isIOS;
  if (isIOS) return "ios";
  if (isIOSChrome) return "ios-chrome";
  if (isIOSFirefox) return "ios-firefox";
  if (isSamsung) return "samsung";
  if (isFirefoxAndroid) return "firefox-android";
  if (isAndroid) return "android";
  if (isEdge) return "edge";
  if (isChromeDesktop) return "chrome-desktop";
  if (isSafariDesktop) return "safari-desktop";
  return "unknown";
}

const PLATFORM_LABEL = {
  ios: "iPhone · Safari",
  "ios-chrome": "iPhone · Chrome",
  "ios-firefox": "iPhone · Firefox",
  samsung: "Samsung Internet",
  "firefox-android": "Android · Firefox",
  android: "Android · Chrome",
  "chrome-desktop": "Desktop · Chrome",
  edge: "Desktop · Edge",
  "safari-desktop": "macOS · Safari",
  unknown: "Your browser",
};

const PLATFORMS = [
  { key: "android",         label: "Android · Chrome" },
  { key: "ios",             label: "iPhone · Safari" },
  { key: "samsung",         label: "Samsung Internet" },
  { key: "firefox-android", label: "Android · Firefox" },
  { key: "chrome-desktop",  label: "Desktop · Chrome" },
  { key: "edge",            label: "Desktop · Edge" },
  { key: "safari-desktop",  label: "macOS · Safari" },
  { key: "ios-chrome",      label: "iPhone · Chrome / Firefox" },
];

function Step({ n, icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C85A53] to-[#7C3AED] text-white text-sm font-bold grid place-items-center shrink-0">{n}</div>
      <div className="flex-1 text-sm text-[color:var(--umg-text)] leading-relaxed pt-1">{text}</div>
      {Icon && <Icon className="w-4 h-4 text-[color:var(--umg-text-2)] mt-2 shrink-0" />}
    </div>
  );
}

function PlatformSteps({ platform }) {
  switch (platform) {
    case "ios":
      return (
        <div className="space-y-4">
          <Step n={1} icon={Share2} text={<>Tap the <b>Share</b> icon at the bottom of Safari</>} />
          <Step n={2} icon={Plus} text={<>Scroll and tap <b>"Add to Home Screen"</b></>} />
          <Step n={3} text={<>Tap <b>Add</b> — Umaang appears on your home screen ✨</>} />
        </div>
      );
    case "ios-chrome":
    case "ios-firefox":
      return (
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--umg-text-2)]">On iPhone / iPad, install works only via <b>Safari</b>.</p>
          <Step n={1} icon={Share2} text={<>Open <b>umaang.co</b> in the Safari browser</>} />
          <Step n={2} icon={Share2} text={<>Tap <b>Share</b> → <b>Add to Home Screen</b></>} />
        </div>
      );
    case "samsung":
      return (
        <div className="space-y-4">
          <Step n={1} icon={MoreVertical} text={<>Tap the <b>menu</b> (three lines) at the bottom right</>} />
          <Step n={2} icon={ArrowUpFromLine} text={<>Tap <b>"Add page to"</b> → <b>"Home screen"</b></>} />
          <Step n={3} text={<>Confirm — Umaang icon added to your home screen ✨</>} />
        </div>
      );
    case "firefox-android":
      return (
        <div className="space-y-4">
          <Step n={1} icon={MoreVertical} text={<>Tap the <b>three dots</b> menu (top right)</>} />
          <Step n={2} text={<>Tap <b>"Install"</b> or <b>"Add to Home Screen"</b></>} />
        </div>
      );
    case "android":
      return (
        <div className="space-y-4">
          <Step n={1} icon={MoreVertical} text={<>Tap the <b>three dots</b> menu (top-right of Chrome)</>} />
          <Step n={2} icon={Download} text={<>Tap <b>"Install app"</b> or <b>"Add to Home Screen"</b></>} />
          <Step n={3} text={<>Confirm — Umaang icon added to your home screen ✨</>} />
          <p className="text-[11px] text-[color:var(--umg-text-2)] pt-1">Not seeing "Install app"? Refresh the page and browse a bit — Chrome shows it after some engagement.</p>
        </div>
      );
    case "chrome-desktop":
    case "edge":
      return (
        <div className="space-y-4">
          <Step n={1} icon={Download} text={<>Look for the <b>Install</b> icon <span className="inline-block px-1 py-0.5 border rounded ml-1"><Monitor className="inline w-3 h-3" /></span> at the right end of the address bar</>} />
          <Step n={2} text={<>Click it → then <b>Install</b></>} />
          <Step n={3} text={<>Or use the browser menu → <b>"Install Umaang…"</b></>} />
        </div>
      );
    case "safari-desktop":
      return (
        <div className="space-y-4">
          <Step n={1} icon={Share2} text={<>Click <b>File</b> menu → <b>Add to Dock</b> (macOS Sonoma+)</>} />
          <Step n={2} text={<>Or click the <b>Share</b> button → <b>Add to Dock</b></>} />
        </div>
      );
    default:
      return (
        <div className="space-y-4">
          <Step n={1} icon={MoreVertical} text={<>Open your browser's <b>menu</b> (three dots or lines)</>} />
          <Step n={2} icon={Download} text={<>Look for <b>"Install app"</b> or <b>"Add to Home Screen"</b></>} />
          <p className="text-[11px] text-[color:var(--umg-text-2)] pt-1">Different browsers have slightly different steps — but every modern browser can install Umaang.</p>
        </div>
      );
  }
}

export default function InstallApp() {
  const detected = detectPlatform();
  const [platform, setPlatform] = useState(detected);
  useEffect(() => { setPlatform(detected); }, [detected]);
  const [installed, setInstalled] = useState(false);
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (standalone) setInstalled(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  return (
    <div className="relative overflow-hidden">
      {/* Decorative color blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 w-96 h-96 rounded-full bg-gradient-to-br from-[#FCA5A5] via-[#F59E0B]/50 to-transparent blur-3xl opacity-70" />
      <div aria-hidden className="pointer-events-none absolute top-40 -right-20 w-96 h-96 rounded-full bg-gradient-to-br from-[#DDD6FE] via-[#F9A8D4]/60 to-transparent blur-3xl opacity-70" />

      <div className="max-w-5xl mx-auto px-6 py-12 relative">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-[color:var(--umg-text-2)] hover:text-[#C85A53]" data-testid="install-back">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>

        <div className="mt-6 grid md:grid-cols-2 gap-10 items-start">
          {/* Left: hero */}
          <div>
            <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">Install App</div>
            <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Umaang on your{" "}
              <span className="bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent">home screen</span>
            </h1>
            <p className="mt-4 text-[color:var(--umg-text-2)] leading-relaxed max-w-md">
              Install Umaang like a native app. One tap to open. Push alerts for new matches. Works offline. No Play Store or App Store needed — install directly from your browser.
            </p>

            {installed ? (
              <div data-testid="install-already" className="mt-8 p-5 border rounded-2xl inline-flex items-center gap-3" style={{ borderColor: "var(--umg-border)", background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)" }}>
                <Sparkles className="w-5 h-5 text-[#059669]" />
                <div className="text-sm text-[#065F46] font-semibold">You're using the installed app — enjoy ✨</div>
              </div>
            ) : (
              <div className="mt-8 flex flex-wrap gap-3 items-center">
                <InstallAppButton testId="install-page-cta" label="Install now" className="h-12 px-6 text-base" />
                <span className="text-xs text-[color:var(--umg-text-2)]">Detected: <b>{PLATFORM_LABEL[detected] || "Your browser"}</b></span>
              </div>
            )}

            {/* Benefits */}
            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              {[
                { icon: Zap,     label: "Instant launch",  color: "#F59E0B" },
                { icon: Bell,    label: "Push alerts",     color: "#DB2777" },
                { icon: WifiOff, label: "Works offline",   color: "#2563EB" },
                { icon: Smartphone, label: "Fullscreen", color: "#7C3AED" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${b.color}18`, color: b.color }}>
                    <b.icon className="w-4 h-4" />
                  </div>
                  <span>{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: logo mock in phone frame */}
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#F59E0B]/25 via-[#DB2777]/20 to-[#7C3AED]/20 blur-2xl" aria-hidden />
            <div className="relative mx-auto w-[260px] h-[520px] rounded-[36px] border-[10px] border-[#1C1917] bg-[color:var(--umg-surface)] shadow-2xl overflow-hidden">
              <div className="h-6 bg-[#1C1917] rounded-b-2xl mx-auto w-24 -mt-[8px]" />
              <div className="p-6 h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#FEF3C7]/40 via-transparent to-[#F9A8D4]/40">
                <img src="/logo-full.png" alt="Umaang" className="w-40 h-auto drop-shadow-xl" draggable="false" />
                <div className="mt-4 text-[10px] uppercase tracking-widest text-[#7C2D12]/70">EK RISHTA · EK UMAANG</div>
                <button
                  onClick={() => window.__umaangInstall?.open?.()}
                  className="mt-8 h-11 px-5 rounded-full bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white text-sm font-semibold shadow-lg shadow-[#DB2777]/40"
                  data-testid="install-phone-mock-cta"
                >
                  <Download className="w-4 h-4 inline mr-1.5" /> Add to home screen
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Platform-specific instructions with pill selector */}
        <div className="mt-16">
          <h2 className="font-display text-2xl md:text-3xl">Step-by-step guide</h2>
          <p className="text-sm text-[color:var(--umg-text-2)] mt-2">We detected <b>{PLATFORM_LABEL[detected] || "your browser"}</b>. Choose a different one if needed:</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {PLATFORMS.map((p) => {
              const active = platform === p.key;
              return (
                <button
                  key={p.key}
                  data-testid={`platform-tab-${p.key}`}
                  onClick={() => setPlatform(p.key)}
                  className={`text-xs px-3.5 py-1.5 rounded-full border transition ${
                    active
                      ? "bg-gradient-to-r from-[#C85A53] to-[#7C3AED] text-white border-transparent shadow-md shadow-[#DB2777]/30"
                      : "border-[color:var(--umg-border)] hover:bg-[color:var(--umg-secondary)]"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="mt-8 p-6 md:p-8 border relative overflow-hidden" style={{ borderRadius: 16, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
            <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10 bg-[#DB2777]" />
            <div className="relative">
              <PlatformSteps platform={platform} />
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="font-display text-2xl md:text-3xl">Quick FAQ</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {[
              { q: "Is it a real app?", a: "Yes — after installing, Umaang runs fullscreen, has its own home-screen icon, and receives push notifications like any native app." },
              { q: "Do I need Play Store or App Store?", a: "No. Installation happens straight from the browser. No store review, no waiting." },
              { q: "Is there any extra cost?", a: "Absolutely not. Installing the app is free, and features stay the same as the website." },
              { q: "How do I uninstall?", a: "Long-press the app icon on your home screen → Uninstall / Remove. Just like any other app." },
            ].map((f) => (
              <div key={f.q} className="p-5 border" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
                <div className="font-semibold text-[color:var(--umg-text)]">{f.q}</div>
                <div className="text-sm text-[color:var(--umg-text-2)] mt-2 leading-relaxed">{f.a}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        {!installed && (
          <div className="mt-14 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-lg shadow-[#DB2777]/30">
            <div>
              <div className="font-display text-2xl">Ready when you are.</div>
              <div className="text-sm opacity-90 mt-1">Install Umaang now — takes 3 taps.</div>
            </div>
            <button
              data-testid="install-bottom-cta"
              onClick={() => window.__umaangInstall?.open?.()}
              className="btn-pill bg-white text-[#B91C1C] hover:bg-white/90 h-11 px-6 font-semibold rounded-full"
            >
              <Download className="w-4 h-4 inline mr-1.5" /> Install now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
